// FluxModal.tsx
import axios from 'axios'
import moment from 'moment'
import * as React from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Button, Checkbox, Dropdown, Header, Icon, Message, Modal } from 'semantic-ui-react'
import Icons from '../../../../assets/icons'

export type FileEntry = {
  date: number
  band: string
  fileUrl: string
  reportType: string // 'BURST' albo 'FLUX' (wg backendu)
  dataType: string // 'RAW', 'HOURLY_AVG', 'DAILY_AVG', 'MINUTELY_AVG', 'EXPLOSION'
}

type Props = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  files?: FileEntry[]
}

type SortDirection = 'desc' | 'asc'

const MAX_SELECTION = 10
const ZIP_DOWNLOAD_ERROR = 'Failed to download zip file'

export default function FluxModal({ open, setOpen, files = [] }: Props) {
  const [copiedLink, setCopiedLink] = React.useState<string | null>(null)
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('desc')

  // Multi-selection state
  const [selectedFiles, setSelectedFiles] = React.useState<Set<string>>(new Set())
  const [isDownloading, setIsDownloading] = React.useState(false)
  const [downloadError, setDownloadError] = React.useState<string | null>(null)

  const getFileKey = (file: FileEntry, index: number) =>
    `${index}-${file.date}-${file.band}-${file.reportType}-${file.dataType}-${file.fileUrl}`

  const sortedFiles = React.useMemo(
    () =>
      files
        .map((file, index) => ({ file, key: getFileKey(file, index) }))
        .sort((a, b) => (sortDirection === 'desc' ? b.file.date - a.file.date : a.file.date - b.file.date)),
    [files, sortDirection],
  )

  // Resetuj „Copied!" gdy modal się zamyka albo zmienia się lista plików
  React.useEffect(() => {
    if (!open) {
      setCopiedLink(null)
      setSelectedFiles(new Set())
      setDownloadError(null)
    }
  }, [open])

  React.useEffect(() => {
    setCopiedLink(null)
    setSelectedFiles(new Set())
    setDownloadError(null)
  }, [files])

  // Selection logic functions
  const toggleFileSelection = (key: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev)
      if (newSet.has(key)) {
        newSet.delete(key)
      } else if (newSet.size < MAX_SELECTION) {
        newSet.add(key)
      }
      return newSet
    })
  }

  const selectAll = () => {
    const newSet = new Set<string>()
    for (let i = 0; i < Math.min(sortedFiles.length, MAX_SELECTION); i++) {
      newSet.add(sortedFiles[i].key)
    }
    setSelectedFiles(newSet)
  }

  const clearSelection = () => {
    setSelectedFiles(new Set())
  }

  // Extract the "path" query param from a fileUrl
  // e.g. "https://rosie-file-downloader.itti.com.pl:8080/download?path=28_12_2025/F10FLUX_20251228_070000009.CSV"
  //  → "28_12_2025/F10FLUX_20251228_070000009.CSV"
  const extractPath = (fileUrl: string): string | null => {
    try {
      const url = new URL(fileUrl)
      return url.searchParams.get('path')
    } catch {
      return null
    }
  }

  // Get base URL of file-downloader from the first file's URL
  const getDownloaderBaseUrl = (): string | null => {
    if (files.length === 0) return null
    try {
      const url = new URL(files[0].fileUrl)
      return `${url.protocol}//${url.host}`
    } catch {
      return null
    }
  }

  // Zip download function - uses backend POST /download-zip endpoint
  const downloadSelectedAsZip = async () => {
    if (selectedFiles.size === 0) return

    setIsDownloading(true)
    setDownloadError(null)

    try {
      // Filter out FTP files and extract paths
      const selectedFileObjects = files
        .map((file, index) => ({ file, key: getFileKey(file, index) }))
        .filter(item => selectedFiles.has(item.key))
        .map(item => item.file)
        .filter(file => !file.fileUrl.startsWith('ftp://'))

      if (selectedFileObjects.length === 0) {
        setDownloadError('Selected files contain only FTP links. FTP files cannot be downloaded as zip.')
        setIsDownloading(false)
        return
      }

      const paths = selectedFileObjects
        .map(file => extractPath(file.fileUrl))
        .filter((p): p is string => p !== null)

      if (paths.length === 0) {
        setDownloadError('Could not extract file paths from selected files.')
        setIsDownloading(false)
        return
      }

      const baseUrl = getDownloaderBaseUrl()
      if (!baseUrl) {
        setDownloadError('Could not determine download server URL.')
        setIsDownloading(false)
        return
      }

      const response = await axios.post(
        `${baseUrl}/download-zip`,
        { paths },
        {
          responseType: 'blob',
          timeout: 60000
        }
      )

      // Trigger browser download
      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = `archive_${moment().format('YYYYMMDD_HHmmss')}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      clearSelection()
    } catch (err: any) {
      console.error('Zip download error:', err)
      if (err.response && err.response.data) {
        // Backend returns JSON error inside a blob - parse it
        try {
          const text = await err.response.data.text()
          const json = JSON.parse(text)
          setDownloadError(json.detail || ZIP_DOWNLOAD_ERROR)
        } catch {
          setDownloadError(ZIP_DOWNLOAD_ERROR)
        }
      } else {
        setDownloadError(err instanceof Error ? err.message : ZIP_DOWNLOAD_ERROR)
      }
    } finally {
      setIsDownloading(false)
    }
  }

  // Helper function to format data type for display
  const formatDataType = (dataType: string): string => {
    switch (dataType) {
      case 'RAW':
        return 'RAW'
      case 'HOURLY_AVG':
        return 'HOUR_AVG'
      case 'DAILY_AVG':
        return 'DAY_AVG'
      case 'MINUTELY_AVG':
        return 'Second_averaging'
      case 'EXPLOSION':
        return 'Raw_burst'
      default:
        return dataType
    }
  }

  // Helper function to calculate duration
  const getDuration = (file: FileEntry): string => {
    // BURST files always have 5 minutes duration
    if (file.reportType === 'BURST' || file.dataType === 'EXPLOSION') {
      return '5m'
    }

    // FLUX files duration based on data type
    switch (file.dataType) {
      case 'RAW':
      case 'MINUTELY_AVG':
        return '1h'
      case 'HOURLY_AVG':
        return '24h'
      case 'DAILY_AVG':
        // For daily average, duration equals requested time range
        // TODO: Pass time range from parent component
        return '1 day'
      default:
        return '1h'
    }
  }

  const renderTile = (file: FileEntry, fileKey: string) => {
    const isFtp = file.fileUrl.startsWith('ftp://')
    const isS3 = file.fileUrl.startsWith('https://s3.amazonaws.com/') || file.fileUrl.includes('.amazonaws.com/')
    const isHttp = file.fileUrl.startsWith('http://') || file.fileUrl.startsWith('https://')
    const isSelected = selectedFiles.has(fileKey)
    const canSelect = selectedFiles.size < MAX_SELECTION || isSelected

    // Etykieta:
    // BURST → "10 cm"/"30 cm"
    // FLUX  → "10_7"/"F30" (bez "cm")
    const label = file.reportType === 'BURST' ? `${file.band.includes('F30') ? '30' : '10'} cm` : file.band === 'F107' ? '10_7' : file.band

    // Format data type for display
    const dataTypeLabel = file.dataType ? formatDataType(file.dataType) : ''

    const dataTypeSuffix = dataTypeLabel ? `(${dataTypeLabel})` : ''
    const copiedSuffix = copiedLink === fileKey ? 'Copied!' : ''
    const fileLabel =
      `${moment.unix(file.date).format('DD-MM-YYYY')} (${label}) ${dataTypeSuffix} ${copiedSuffix}`.trim()

    // Get duration for this file
    const duration = getDuration(file)

    // Common content structure with checkbox
    const tileContent = (
      <div className={`file_button ${isSelected ? 'file_button_selected' : ''}`} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Checkbox
          checked={isSelected}
          onChange={() => toggleFileSelection(fileKey)}
          disabled={!canSelect && !isSelected}
          style={{ marginRight: 10, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        />
        <Icons.FileIcon />
        <div className='margin-left-20' style={{ flex: 1 }}>
          <Header as='h5' className='margin_bottom_5'>
            {fileLabel}
          </Header>
          <div>{`Beginning time: ${moment.unix(file.date).utc().format('HH:mm')} UTC`}</div>
          <div>{`Duration: ${duration}`}</div>
        </div>
      </div>
    )

    // FTP → kopiuj link
    if (isFtp) {
      return (
        <div key={`wrapper-${fileKey}`} style={{ position: 'relative', width: '32%' }}>
          {tileContent}
          <CopyToClipboard text={file.fileUrl} onCopy={() => setCopiedLink(fileKey)}>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 50,
                right: 0,
                bottom: 0,
                cursor: 'pointer'
              }}
              title='Copy FTP link'
            />
          </CopyToClipboard>
        </div>
      )
    }

    // HTTP/S3 → pobieranie/otwarcie w nowej karcie
    if (isS3 || isHttp) {
      return (
        <div key={`wrapper-${fileKey}`} style={{ position: 'relative', width: '32%' }}>
          {tileContent}
          <a
            href={file.fileUrl}
            download
            target='_blank'
            rel='noopener noreferrer'
            style={{
              position: 'absolute',
              top: 0,
              left: 50,
              right: 0,
              bottom: 0,
              textDecoration: 'none',
              cursor: 'pointer'
            }}
            title='Open file in a new tab'
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )
    }

    // Fallback – nieznany protokół
    return (
      <div key={fileKey} style={{ width: '32%' }}>
        {tileContent}
      </div>
    )
  }

  const isEmpty = open && files.length === 0

  return (
    <Modal open={open} onClose={() => setOpen(false)} closeIcon size='large'>
      <Modal.Header>Files</Modal.Header>
      <Modal.Content scrolling>
        {!isEmpty && (
          <>
            <Message info size='small'>
              <Message.Content>
                Found {files.length} file{files.length !== 1 ? 's' : ''} in archive
              </Message.Content>
            </Message>

            {/* Selection Controls */}
            <div style={{ marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <Dropdown
                selection
                compact
                options={[
                  { key: 'desc', text: 'Newest first', value: 'desc' },
                  { key: 'asc', text: 'Oldest first', value: 'asc' },
                ]}
                value={sortDirection}
                onChange={(e, d) => setSortDirection(d.value as SortDirection)}
              />
              <Button size='small' onClick={selectAll} disabled={files.length === 0}>
                Select All (max {MAX_SELECTION})
              </Button>
              <Button size='small' onClick={clearSelection} disabled={selectedFiles.size === 0}>
                Clear Selection
              </Button>
              <span style={{ marginLeft: 10, fontWeight: 'bold' }}>
                {selectedFiles.size} / {MAX_SELECTION} selected
              </span>
              {selectedFiles.size >= MAX_SELECTION && (
                <Message warning size='tiny' style={{ margin: 0, padding: '5px 10px', display: 'inline-block' }}>
                  Maximum {MAX_SELECTION} files can be selected
                </Message>
              )}
            </div>
          </>
        )}

        {isEmpty && (
          <Message info size='small'>
            <Message.Content>No files for selected range.</Message.Content>
          </Message>
        )}

        {/* Error message */}
        {downloadError && (
          <Message negative onDismiss={() => setDownloadError(null)}>
            <Message.Header>Download Error</Message.Header>
            <p>{downloadError}</p>
          </Message>
        )}

        <div style={{ display: 'flex', flexFlow: 'wrap', gap: 10, justifyContent: 'flex-start' }}>
          {sortedFiles.map(({ file, key }) => renderTile(file, key))}
        </div>

        <span aria-live='polite' style={{ position: 'absolute', left: -9999 }}>
          {copiedLink ? 'Link copied' : ''}
        </span>
      </Modal.Content>

      {/* Modal Actions */}
      <Modal.Actions>
        <Button
          primary
          disabled={selectedFiles.size === 0 || isDownloading}
          loading={isDownloading}
          onClick={downloadSelectedAsZip}
          icon
          labelPosition='left'
        >
          <Icon name='download' />
          Download Selected as ZIP ({selectedFiles.size} {selectedFiles.size === 1 ? 'file' : 'files'})
        </Button>
        <Button onClick={() => setOpen(false)}>Close</Button>
      </Modal.Actions>
    </Modal>
  )
}
